import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const chipVariants = cva(
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
    {
        variants: {
            variant: {
                default:
                    "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
                secondary:
                    "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
                destructive:
                    "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
                outline: "text-foreground",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
)

export interface ChipProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof chipVariants> {
    onRemove?: () => void
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
    ({ className, variant, onRemove, children, ...props }, ref) => (
        <div
            className={cn(chipVariants({ variant }), className)}
            ref={ref}
            {...props}
        >
            {children}
            {onRemove && (
                <button
                    type="button"
                    className="ml-1 rounded-full outline-offset-2 hover:bg-secondary"
                    onClick={onRemove}
                >
                    <span className="sr-only">Remove</span>
                    <svg
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}
        </div>
    )
)
Chip.displayName = "Chip"

export { Chip, chipVariants }